import React, { useEffect, useState } from 'react'
import { Platform } from 'react-native'

import { useToast } from '@zet/common/components/toast'
import { useNavigation } from '@zet/common/hooks/useNavigation'
import useAddCustomerStore from '@zet/customer/src/store/addCustomer'
import { webStates } from '@zet/customer/src/utils/addCustomerStates'
import { Stack } from '@zet/design-system'
import ScreenLoader from '@zet/feature-brands/screens/brandListV2/screenLoader'
import { i18n } from '@zet/localization'
import { replaceLoginHash } from '@zet/utils/loginHash'
import useGetAgentDetailsAndTheme from '@zet/zet-pro-quick-journey/hooks/useGetAgentDetailsAndTheme'
import _ from 'lodash'

import { useGetDecryptToken } from '../../hooks/useGetDescrptJwt'
import useJPBBridge from '../../hooks/useJPBBridge'
import { useJwtToken } from '../../hooks/useJwtToken'
import useNativeAppBridge from '../../hooks/useNativeAppBridge'
import { isWebView } from '../../utils'
import { sendAnalyticsData } from '../../utils/analyticsEvents'
import {
  getJfsToken,
  getJfsTokenFromUrl,
  removeQueryParams,
  setJfsCustomerId,
  setJfsCustomerPhoneNumber,
  setJfsToken,
} from '../../utils/jfsSession'

export const CustomerLoading = () => {
  const [token, setToken] = useState<string>(getJfsToken() || '')
  const [hasBootstrapError, setHasBootstrapError] = useState(false)
  const { getJwtToken } = useJwtToken()
  const { isIOS } = useJPBBridge()
  const { changeFooterVisibility, changeHeaderVisibility } =
    useNativeAppBridge()
  const { isLoading: isThemeLoading, isJfsFlow } = useGetAgentDetailsAndTheme()
  const { setCustomerId, setCustomerPhoneNumber, setVariantId } =
    useAddCustomerStore()
  const navigation = useNavigation()
  const toast = useToast()

  const showErrorToast = (message?: string) => {
    toast?.show({
      intent: 'error',
      heading: i18n.t('toastMessages.error'),
      description: message || i18n.t('toastMessages.somethingWentWrong'),
      hideAfter: 3000,
    })
  }

  useEffect(() => {
    const fetchToken = async () => {
      const urlToken = getJfsTokenFromUrl()
      const persistedToken = getJfsToken()

      if (urlToken) {
        setJfsToken(urlToken)
        setToken(urlToken)
        setHasBootstrapError(false)
        return
      }
      if (persistedToken && !isWebView()) {
        setToken(persistedToken)
        setHasBootstrapError(false)
        return
      }
      if (!isWebView()) {
        setHasBootstrapError(true)
        showErrorToast('Missing token for JFS desktop web flow')
        return
      }

      try {
        const response = await getJwtToken()
        await changeFooterVisibility(0)
        await changeHeaderVisibility(0)
        let parsedResponse = response
        if (!isIOS()) {
          parsedResponse = JSON.parse(response)
        }
        const jwt = isIOS() ? parsedResponse : parsedResponse?.data?.jwtToken
        if (!jwt) throw new Error('JWT not found in response')
        setJfsToken(jwt)
        setToken(jwt)
      } catch (err) {
        console.error('Failed to get JWT token:', err)
        setHasBootstrapError(true)
        showErrorToast()
      }
    }

    fetchToken()
  }, [])

  const {
    data: decodedToken,
    isLoading: isTokenDecrypting,
    error: decryptError,
  } = useGetDecryptToken(token)

  useEffect(() => {
    if (decryptError) {
      console.error('Token decryption failed:', decryptError)
      setHasBootstrapError(true)
      showErrorToast()
    }
  }, [decryptError])

  useEffect(() => {
    if (!decodedToken) return

    const urlParams = new URLSearchParams(window.location.search)
    const variantId = encodeURIComponent(urlParams.get('productId') || '')
    const benefitsParam = urlParams.get('benefits')
    const quickFilterParam = urlParams.get('quickFilter')
    const cardFeesParam = urlParams.get('cardFees')

    const { action, actionData } = decodedToken
    const { phoneNumber, customerId, jfsConsent, hash = '', subscribed } = actionData || {}

    if (subscribed !== undefined && subscribed !== null) {
      sessionStorage.setItem('jfsSubscribed', String(subscribed))
    }
    if (hash) {
      replaceLoginHash(hash)
      const decodedHash = decodeURIComponent(hash)
      if (typeof window !== 'undefined') {
        const url = new URL(window.location.href)
        url.searchParams.set('hash', decodedHash)
        window.history.replaceState({}, '', url.toString())
      }
    }

    if (_.isObject(jfsConsent)) {
      sessionStorage.setItem('jfsConsent', JSON.stringify(jfsConsent))
    }

    if (phoneNumber) {
      setCustomerPhoneNumber(phoneNumber)
      setJfsCustomerPhoneNumber(phoneNumber)
    }

    if (action) {
      const actionKey = action as keyof typeof webStates
      const targetScreen = webStates[actionKey]

      if (!targetScreen) {
        console.warn('Unknown action or missing navigation path', action)
        return
      }

      const navigationParams: Record<string, any> = { phoneNumber }

      // Only pass isJfsFlow as a param on web to avoid affecting mobile flow
      if (Platform.OS === 'web') {
        navigationParams.isJfsFlow = isJfsFlow ?? false
      }

      if (variantId) {
        navigationParams.variantId = variantId
        navigationParams.categoryId = 15
        setVariantId(Number(variantId))
      }

      const subFiltersFromUrl: Record<string, string[]> = {}
      if (benefitsParam) {
        subFiltersFromUrl.benefits = benefitsParam.split(',')
      }
      if (cardFeesParam) {
        subFiltersFromUrl.cardFees = cardFeesParam.split(',')
      }
      if (Object.keys(subFiltersFromUrl).length > 0) {
        navigationParams.subFilters = JSON.stringify(subFiltersFromUrl)
      }

      if (quickFilterParam) {
        navigationParams.quickFilter = quickFilterParam.split(',')
      }

      if (customerId !== undefined) {
        setCustomerId(customerId)
        setJfsCustomerId(customerId)
        navigationParams.customerId = customerId
      } else {
        sessionStorage.removeItem('categoryId')
      }

      removeQueryParams(['token', 'phoneNumber', 'benefits', 'quickFilter', 'cardFees'])
      sendAnalyticsData({ screen: action, type: 'screen' })
      navigation.replace(targetScreen, navigationParams)
    } else {
      console.warn('Unknown action or missing navigation path', action)
      setHasBootstrapError(true)
      showErrorToast()
    }
  }, [decodedToken])

  if (
    isThemeLoading ||
    isTokenDecrypting ||
    (!decodedToken && !hasBootstrapError)
  ) {
    return (
      <Stack className="flex-1 items-center justify-center bg-white">
        <ScreenLoader showLottie isJFSFlow />
      </Stack>
    )
  }

  return <Stack className="flex-1 bg-white" />
}
